import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Copy, Trash2, Users } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage({ user, club, onLogout }) {
  const navigate = useNavigate();
  const [clubInfo, setClubInfo] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  useEffect(() => {
    loadClubInfo();
  }, []);

  const loadClubInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [infoRes, membersRes] = await Promise.all([
        axios.get(`${API}/club/info`, { headers }),
        axios.get(`${API}/club/team-members`, { headers }).catch(() => ({ data: [] }))
      ]);

      setClubInfo(infoRes.data);
      setTeamMembers(membersRes.data);
    } catch (error) {
      console.error('Failed to load club info:', error);
      if (error.response?.status === 403) {
        // Not a parent account, only show club info
        setTeamMembers([]);
      } else {
        toast.error('Failed to load club information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (clubInfo?.clubCode) {
      navigator.clipboard.writeText(clubInfo.clubCode);
      toast.success('Club code copied to clipboard!');
    }
  };

  const handleChangeEmail = async (newEmail) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(`${API}/club/change-email`, { newEmail }, { headers });
      toast.success('Email updated successfully! Please login with your new email.');
      
      // Logout after email change
      setTimeout(() => {
        onLogout();
      }, 2000);
    } catch (error) {
      console.error('Failed to change email:', error);
      toast.error(error.response?.data?.detail || 'Failed to change email');
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(`${API}/club/team-members/${memberToRemove.id}`, { headers });
      toast.success('Team member removed successfully');
      setShowRemoveDialog(false);
      setMemberToRemove(null);
      loadClubInfo();
    } catch (error) {
      console.error('Failed to remove team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-12 sm:h-16 w-auto" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-black" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  Club Settings
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">{club?.name || user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5] flex-1 sm:flex-none"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button
                onClick={onLogout}
                variant="ghost"
                className="text-gray-700 hover:text-[#000000] flex-1 sm:flex-none"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Club Code Card */}
          <Card>
            <CardHeader>
              <CardTitle>Club Code</CardTitle>
              <CardDescription>
                Share this code with team members to give them access to your club account
              </CardDescription>
            </CardHeader>
            <CardContent>
              {clubInfo?.clubCode ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-4 bg-gray-100 rounded-lg font-mono text-2xl font-bold text-center tracking-wider">
                      {clubInfo.clubCode}
                    </div>
                    <Button
                      onClick={handleCopyCode}
                      variant="outline"
                      size="icon"
                      className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5]"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600">
                    This code never expires and can be used by multiple team members. They will have access to all club data and can enter competitions on your behalf.
                  </p>
                </div>
              ) : (
                <p className="text-gray-600">No club code available</p>
              )}
            </CardContent>
          </Card>

          {/* Change Email Card */}
          <Card>
            <CardHeader>
              <CardTitle>Email Address</CardTitle>
              <CardDescription>
                Update your club's email address. This will update your login email.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Current Email</label>
                  <p className="mt-1 text-gray-900">{clubInfo?.email || user.email}</p>
                </div>
                <Button
                  onClick={() => {
                    const newEmail = prompt('Enter new email address:', clubInfo?.email || user.email);
                    if (newEmail && newEmail !== clubInfo?.email) {
                      handleChangeEmail(newEmail);
                    }
                  }}
                  variant="outline"
                  className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5]"
                >
                  Change Email Address
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Team Members Card */}
          {clubInfo?.isParentAccount && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Team Members ({teamMembers.length})
                </CardTitle>
                <CardDescription>
                  Manage users who have access to your club account via club code
                </CardDescription>
              </CardHeader>
              <CardContent>
                {teamMembers.length === 0 ? (
                  <p className="text-gray-600">No team members have joined yet</p>
                ) : (
                  <div className="space-y-3">
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div>
                          <p className="font-semibold">{member.email}</p>
                          <p className="text-sm text-gray-600">
                            Joined {new Date(member.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            setMemberToRemove(member);
                            setShowRemoveDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-gray-900 border-gray-900 hover:bg-gray-100"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Remove Team Member Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {memberToRemove?.email} from your club? They will lose access immediately and will need to sign up again with the club code to regain access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setMemberToRemove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-gray-900 hover:bg-black"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
